import React, { useRef, useState, useEffect, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18n-js';
import { buildUrl } from 'utils';
import Grid from '@mui/material/Grid';
import Wizard from 'components/UI/Wizard';
import { OnboardingRoot, stepperContainerClass, contentClass } from './Onboarding.styled';
import { uiActions } from 'state/modules/UI';
import { useHistory } from 'react-router-dom';
import { abilityConfig } from 'config/ability';
import { useDispatch, useSelector } from 'react-redux';
import { Details, VehiclesGroupsServices, Locations } from 'components/Fleets';
import { Rulesets, VehiclesGroupsPricing } from 'components/VehiclesGroups';
import { VehiclesGroupsActions } from 'state/modules/vehiclesGroups';
import { ReservationsActions } from 'state/modules/reservations';
import { useConfirm } from 'hooks';

const FleetsOnboarding = ({ forwardRef, mode, onSave }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { rules } = useSelector((state) => state?.reservations);
  const formRef = useRef(null);
  const [edit, setEdit] = useState(false);
  const [pricing, setPricing] = useState('');
  const [showSaveAndExitModal, setShowSaveAndExitModal] = useState(false);
  const successMessage = i18n.t(
    `vehicle_groups.${mode === 'create' ? 'fleet_created_feedback' : 'fleet_updated_feedback'}`
  );
  const hasReservations = useSelector(
    (state) => state.config?.settings?.core?.reservation?.enabled || false
  );
  const canCreateReservation = useSelector(
    (state) => state.config?.settings?.core?.reservation?.create || false
  );
  const sharingType = useSelector(
    (state) => state.config?.settings?.core?.partnership?.sharing_type
  );
  const pricingEnabled = useSelector(
    (state) => state.config.settings?.core?.pricing?.enabled || false
  );
  const requiresRuleset = hasReservations && canCreateReservation;

  const stepsList = [
    {
      key: 'details',
      label: i18n.t('common.details'),
    },
    {
      key: 'locations',
      label: i18n.t('common.locations'),
    },
    {
      key: 'services',
      label:
        sharingType === 'FFB'
          ? i18n.t('vehicle_groups.partners')
          : i18n.t('vehicle_groups.services'),
    },
    ...(requiresRuleset
      ? [
          {
            key: 'rulesets',
            label: i18n.t('vehicle_groups.reservations_rulesets'),
          },
        ]
      : []),
    ...(pricingEnabled
      ? [
          {
            key: 'pricing',
            label: i18n.t('common.pricing'),
          },
        ]
      : []),
  ];

  const {
    loading,
    rrsTimes,
    relatedRulesets,
    onboarding: { exitOnboarding, exitStep, position, errors, steps },
  } = useSelector((state) => state.vehiclesGroups);

  const handleNextPosition = () => {
    dispatch(VehiclesGroupsActions.changePosition('next'));
  };

  const handleEndOnboarding = (showFeedback = true) => {
    dispatch(VehiclesGroupsActions.resetOnboarding());
    if (showFeedback) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: successMessage,
        })
      );
    }
    history.push(buildUrl('/fleets'));
  };

  useEffect(() => {
    if (abilityConfig.can('enabled', 'reservation')) {
      dispatch(ReservationsActions.fetchReservationRules({ is_enabled: true }));
    }
  }, [dispatch]);

  useEffect(() => {
    onSave(false);
  }, [position, onSave]);

  const handleNextStep = (exit = false) => {
    switch (position) {
      case 0:
        if (formRef?.current) {
          return formRef.current.submitForm();
        }
        break;
      case 1:
        if (!steps?.locations?.assigned?.list || steps?.locations?.assigned?.list?.length === 0) {
          dispatch(
            uiActions.handleShowSnackbar({
              type: 'error',
              message: i18n.t('vehicle_groups.add_location_service_area'),
            })
          );
        } else if (exit) {
          handleEndOnboarding(exit);
        } else {
          handleNextPosition(exit);
        }
        break;
      case 2:
        if (steps?.services?.assigned?.list?.length === 0) {
          dispatch(
            uiActions.handleShowSnackbar({
              type: 'error',
              message: i18n.t('vehicle_groups.add_service_service_area'),
            })
          );
        } else {
          if (position === stepsList.length - 1) {
            handleEndOnboarding(exit);
          } else {
            handleNextPosition(exit);
          }
        }
        break;
      case 3:
        if (requiresRuleset) {
          handleNextPosition(exit);
        } else {
          handleEndOnboarding();
        }
        break;
      case 4:
        handleEndOnboarding();
        break;
      default:
        break;
    }
  };

  const onSaveAndExit = () => {
    handleNextStep(true);
  };

  const handleExit = (exit) => {
    dispatch(VehiclesGroupsActions.setExit(exit || !exitStep, 'exitStep'));
  };

  const handleShowOnSaveAndExitModal = () => {
    setShowSaveAndExitModal((prevState) => !prevState);
  };

  const handleOnSaveAndExit = () => {
    handleExit(true);
    setShowSaveAndExitModal((prevState) => !prevState);
  };

  useEffect(() => {
    if (exitOnboarding) {
      handleEndOnboarding();
    }
  }, [exitOnboarding]); // eslint-disable-line

  const onPrevious = () => {
    dispatch(VehiclesGroupsActions.changePosition('prev'));
  };

  useImperativeHandle(forwardRef, () => ({
    handleOnSaveAndExit,
  }));

  const setTitle = () => {
    let title = '';
    if (steps?.details) {
      title = i18n.t('vehicle_groups.service_area_name', {
        name: steps.details.name,
        id: steps.details.id,
      });
    } else {
      title = i18n.t('vehicle_groups.new_service_area');
    }

    return title;
  };

  const [toggleNext, NextConfirm] = useConfirm({
    message: i18n.t('vehicle_groups.unsaved_changes'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.continue'),
    onConfirm: handleNextStep,
  });

  const showUnsavedPricing =
    stepsList[position]?.key === 'pricing' &&
    pricing != '' &&
    steps?.pricing?.currentRuleset?.rule_set?.id != pricing;

  return (
    <OnboardingRoot>
      <NextConfirm />
      <Wizard
        exit={exitStep}
        steps={stepsList}
        loading={loading}
        title={setTitle()}
        position={position}
        handleExit={handleExit}
        onNext={showUnsavedPricing ? toggleNext : handleNextStep}
        onPrevious={onPrevious}
        onSaveAndExit={onSaveAndExit}
        successMessage={successMessage}
        handleEndOnboarding={handleEndOnboarding}
        showSaveAndExitModal={showSaveAndExitModal}
        customStepperClass={stepperContainerClass}
        handleShowOnSaveAndExitModal={handleShowOnSaveAndExitModal}
        customContentClass={stepsList[position]?.key === 'pricing' ? contentClass : undefined}
      >
        {stepsList[position]?.key === 'details' && (
          <Details
            exit={exitStep}
            errors={errors}
            onSave={onSave}
            formRef={formRef}
            step={steps.details}
            handleNextPosition={handleNextPosition}
            rules={rules}
          />
        )}
        {stepsList[position]?.key === 'locations' && (
          <Grid container direction="row" justifyContent="center" alignItems="center">
            <Grid size={12}>
              <Locations
                type="service"
                loading={loading}
                step={steps.locations}
                currentGroup={steps.details}
              />
            </Grid>
          </Grid>
        )}
        {stepsList[position]?.key === 'services' && (
          <Grid container direction="row" justifyContent="center" alignItems="center">
            <Grid size={12}>
              <VehiclesGroupsServices fleet={steps.details} isModal />
            </Grid>
          </Grid>
        )}
        {stepsList[position]?.key === 'rulesets' && (
          <Rulesets
            step={rrsTimes}
            onSave={onSave}
            loading={loading}
            currentGroup={steps.details}
          />
        )}
        {stepsList[position]?.key === 'pricing' && (
          <VehiclesGroupsPricing
            loading={loading}
            step={steps?.pricing}
            onSave={onSave}
            currentGroup={steps.details}
            pricingRulesets={relatedRulesets}
            edit={edit}
            setEdit={setEdit}
            pricing={pricing}
            setPricing={setPricing}
            fleet
          />
        )}
      </Wizard>
    </OnboardingRoot>
  );
};

FleetsOnboarding.propTypes = {
  forwardRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  mode: PropTypes.string,
  onSave: PropTypes.func,
};

export default FleetsOnboarding;
